import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { UseApplyComponentActionType } from "@v1/hooks/useApplyComponentAction";
import type { BoxShadow } from "replo-runtime/shared/types";
import type { TransformObject } from "replo-runtime/shared/utils/transform";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import type { ToggleGroupOption, Transform } from "schemas/effects";
import type { SavedColorStyle } from "schemas/generated/designLibrary";
import type { EffectsControlType } from "schemas/modifiers";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import SelectionIndicator from "@v1/components/common/designSystem/SelectionIndicator";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import Popover from "@v1/components/designSystem/popover/Popover";
import Selectable from "@v1/components/designSystem/Selectable";
import { DeprecatedToggleGroup } from "@v1/components/designSystem/toggle/ToggleGroup";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import { transformDefaultValues } from "@v1/components/editor/page/element-editor/components/modifiers/utils";
import useShopStyles from "@v1/hooks/designLibrary/useGetDesignLibrarySavedStyles";
import { useGetModifierControls } from "@v1/hooks/rightBar/useGetModifierControls";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import {
  selectBoxShadows,
  selectCursor,
  selectDraftComponentId,
  selectDraftComponentsPositionKey,
  selectRotation,
  selectTransform,
  selectTransformOrigin,
} from "@v1/reducers/core-reducer";
import { selectOpenPopoverId, setOpenPopoverId } from "@v1/reducers/ui-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { getBoxShadowString } from "@v1/utils/boxShadow";
import {
  getReadableTransformValue,
  getTransformOriginString,
  parseTransformOrigin,
} from "@v1/utils/effects";
import { styleAttributeToEditorData } from "@v1/utils/styleAttribute";
import { useDynamicCommandMenuItem } from "@v2/hooks/useDynamicCommandMenuItems";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useAreModalsOpen } from "@v2/store/modalStore";
import merge from "lodash-es/merge";
import {
  ArrowUpLeft,
  BoxSelect,
  MousePointer,
  PaintBucket,
  RotateCw,
} from "lucide-react";
import { getSavedStyleValue } from "replo-runtime/shared/savedStyles";
import { isDynamicDesignLibraryValue } from "replo-runtime/shared/utils/designLibrary";
import { getTransformStyleString } from "replo-runtime/shared/utils/transform";
import {
  CSS_ANGLE_TYPES,
  CSS_LENGTH_TYPES,
} from "replo-runtime/shared/utils/units";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import { v4 as uuidv4 } from "uuid";

import ModifierLabel from "../extras/ModifierLabel";
import { DynamicColorSelector } from "./DynamicColorModifier";

const DEFAULT_BOX_SHADOW = {
  shadowType: "dropShadow" as const,
  offsetX: "0px",
  offsetY: "4px",
  blur: "4px",
  spread: "0px",
  shadowColor: "#00000040",
};

type EffectsPopoverId = "transform" | "transformOrigin" | null;

const EffectsModifier: React.FC = () => {
  const groupRef = React.useRef<HTMLDivElement>(null);
  const [controls, addControl] = useGetModifierControls<"effects">(
    "effects",
    groupRef,
  );
  const handleAddBoxShadow = useAddNewBoxShadow();
  const [openPopoverId, setOpenPopoverId] =
    React.useState<EffectsPopoverId>(null);

  const menuItems: MenuItem[] = [
    {
      id: "transform",
      title: "Transform",
      variant: "default",
      size: "sm",
      onSelect: () => {
        addControl("transform");
        setOpenPopoverId("transform");
      },
      type: "leaf",
      disabled: controls.has("transform"),
    },
    {
      id: "transformOrigin",
      title: "Transform Origin",
      variant: "default",
      size: "sm",
      onSelect: () => {
        addControl("transformOrigin");
        setOpenPopoverId("transformOrigin");
      },
      type: "leaf",
      disabled: controls.has("transformOrigin"),
    },
    {
      id: "cursor",
      title: "Cursor",
      variant: "default",
      size: "sm",
      onSelect: () => addControl("cursor"),
      type: "leaf",
      disabled: controls.has("cursor"),
    },
    {
      id: "opacity",
      title: "Opacity",
      variant: "default",
      size: "sm",
      onSelect: () => addControl("opacity"),
      type: "leaf",
      disabled: controls.has("opacity"),
    },
    {
      id: "boxShadow",
      title: "Box Shadow",
      variant: "default",
      size: "sm",
      onSelect: () => {
        addControl("boxShadow");
        handleAddBoxShadow();
      },
      type: "leaf",
      disabled: false,
    },
  ];

  const controlsToComponent: Array<{
    id: EffectsControlType;
    component: React.ReactNode;
  }> = [
    {
      id: "transform",
      component: (
        <TransformControl
          isOpen={openPopoverId === "transform"}
          setIsOpen={(isOpen) => setOpenPopoverId(isOpen ? "transform" : null)}
        />
      ),
    },
    {
      id: "transformOrigin",
      component: (
        <TransformOriginControl
          isOpen={openPopoverId === "transformOrigin"}
          setIsOpen={(isOpen) => {
            return setOpenPopoverId(isOpen ? "transformOrigin" : null);
          }}
        />
      ),
    },
    {
      id: "opacity",
      component: <OpacityControl />,
    },
    {
      id: "cursor",
      component: <CursorControl />,
    },
    {
      id: "boxShadow",
      component: <ShadowControl />,
    },
  ];

  const hasControlsToRender = controls.size > 0;

  const onSelectTransform = React.useCallback(() => {
    addControl("transform");
    setOpenPopoverId("transform");
  }, [addControl]);

  const transformDynamicMenuItem = React.useMemo(() => {
    return !controls.has("transform")
      ? ({
          label: "Add Transform",
          group: "add",
          icon: { type: "iconComponent", component: RotateCw },
          onSelect: onSelectTransform,
        } as const)
      : null;
  }, [controls, onSelectTransform]);

  useDynamicCommandMenuItem("transform", transformDynamicMenuItem);

  const onSelectTransformOrigin = React.useCallback(() => {
    addControl("transformOrigin");
    setOpenPopoverId("transformOrigin");
  }, [addControl]);

  const transformOriginDynamicMenuItem = React.useMemo(() => {
    return !controls.has("transformOrigin")
      ? ({
          label: "Add Transform Origin",
          group: "add",
          icon: { type: "iconComponent", component: ArrowUpLeft },
          onSelect: onSelectTransformOrigin,
        } as const)
      : null;
  }, [controls, onSelectTransformOrigin]);

  useDynamicCommandMenuItem("transformOrigin", transformOriginDynamicMenuItem);

  const onSelectCursor = React.useCallback(() => {
    addControl("cursor");
  }, [addControl]);

  const cursorDynamicMenuItem = React.useMemo(() => {
    return !controls.has("cursor")
      ? ({
          label: "Add Cursor",
          group: "add",
          icon: { type: "iconComponent", component: MousePointer },
          onSelect: onSelectCursor,
        } as const)
      : null;
  }, [controls, onSelectCursor]);

  useDynamicCommandMenuItem("cursor", cursorDynamicMenuItem);

  const onSelectOpacity = React.useCallback(() => {
    addControl("opacity");
  }, [addControl]);

  const opacityDynamicMenuItem = React.useMemo(() => {
    return !controls.has("opacity")
      ? ({
          label: "Add Opacity",
          group: "add",
          icon: { type: "iconComponent", component: PaintBucket },
          onSelect: onSelectOpacity,
        } as const)
      : null;
  }, [controls, onSelectOpacity]);

  useDynamicCommandMenuItem("opacity", opacityDynamicMenuItem);

  const onSelectBoxShadow = React.useCallback(() => {
    addControl("boxShadow");
    handleAddBoxShadow();
  }, [addControl, handleAddBoxShadow]);

  const boxShadowDynamicMenuItem = React.useMemo(() => {
    return !controls.has("boxShadow")
      ? ({
          label: "Add Box Shadow",
          group: "add",
          icon: { type: "iconComponent", component: BoxSelect },
          onSelect: onSelectBoxShadow,
        } as const)
      : null;
  }, [controls, onSelectBoxShadow]);

  useDynamicCommandMenuItem("boxShadow", boxShadowDynamicMenuItem);

  return (
    <ModifierGroup
      ref={groupRef}
      title="Effects"
      isDefaultOpen={hasControlsToRender}
      isCollapsible={hasControlsToRender}
      menuItems={menuItems}
      tooltipText="Add Additional Effects"
      // Note (Noah, 2024-10-25): Disable focusing the menu trigger on close, since this will
      // dismiss the popover that opens when we add a box shadow
      disableMenuTriggerFocusOnClose
    >
      <div className="flex flex-col gap-2">
        {controlsToComponent.map((control) => {
          const shouldRender = controls.has(control.id);
          if (!shouldRender) {
            return null;
          }

          return control.component;
        })}
      </div>
    </ModifierGroup>
  );
};

const OpacityControl: React.FC = () => {
  const applyComponentAction = useApplyComponentAction();

  const onChange = (newValue: string) => {
    applyComponentAction({
      type: "setStyles",
      value: { opacity: newValue },
    });
  };

  const opacityDefaultValue = `${
    Number.parseInt(styleAttributeToEditorData.opacity.defaultValue) * 100
  }%`;

  return (
    <div className="flex items-center">
      <LengthInputSelector
        label={<ModifierLabel label="Opacity" />}
        dragTrigger="label"
        menuOptions={["0%", "50%", "100%"].map((v) => ({
          label: v,
          value: v,
        }))}
        placeholder={opacityDefaultValue}
        anchorValue="50%"
        resetValue={opacityDefaultValue}
        metrics={["%"]}
        field="style.opacity"
        onChange={onChange}
        minValues={{ "%": 0 }}
        maxValues={{ "%": 100 }}
        minDragValues={{ "%": 0 }}
        maxDragValues={{ "%": 100 }}
        previewProperty="opacity"
      />
    </div>
  );
};

const cursorSelectableItems = [
  {
    label: "Default",
    value: "default",
  },
  {
    label: "Pointer",
    value: "pointer",
  },
  {
    label: "Crosshair",
    value: "crosshair",
  },
  {
    label: "Not Allowed",
    value: "not-allowed",
  },
  {
    label: "Input",
    value: "text",
  },
  {
    label: "Arrows",
    value: "ew-resize",
  },
  {
    label: "Vertical Arrows",
    value: "ns-resize",
  },
  {
    label: "No Cursor",
    value: "none",
  },
];

const CursorControl: React.FC = () => {
  const applyComponentAction = useApplyComponentAction();
  const value = useEditorSelector(selectCursor);

  return (
    <div className="flex w-full items-center">
      <ModifierLabel label="Cursor" />
      <Selectable
        value={value && value !== "auto" ? value : "default"}
        triggerLayoutClassName="w-full"
        layoutClassName="w-full"
        options={cursorSelectableItems}
        onSelect={(newValue) => {
          applyComponentAction({
            type: "setStyles",
            value: { cursor: newValue },
          });
        }}
      />
    </div>
  );
};

const TransformControl: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const [verticalPosition, horizontalPosition] = useEditorSelector(
    selectDraftComponentsPositionKey,
  );
  const shouldDisableTranslateTab =
    verticalPosition === "center" || horizontalPosition === "center";
  const activeTabInitialValue = shouldDisableTranslateTab ? "scale" : "move";
  const [activeTab, setActiveTab] = React.useState<Transform>(
    activeTabInitialValue,
  );

  const applyComponentAction = useApplyComponentAction();

  const originalTransform = useEditorSelector(selectTransform);
  const draftElementAlchemyRotation = useEditorSelector(selectRotation);
  const transform = !isMixedStyleValue(originalTransform)
    ? merge({}, transformDefaultValues, originalTransform)
    : null;

  const handleApplyTransform = (value: TransformObject) => {
    const actions: UseApplyComponentActionType[] = [
      {
        type: "setStyles",
        value: { __transform: value },
      },
    ];
    // Note (Sebas, 2022-07-20): The __alchemyRotation value is not necessary anymore.
    // If it exists we need to reset it to null
    if (draftElementAlchemyRotation) {
      actions.push({
        type: "setStyles",
        value: {
          __alchemyRotation:
            styleAttributeToEditorData.__alchemyRotation.defaultValue,
        },
      });
    }
    applyComponentAction({
      type: "applyCompositeAction",
      value: actions,
    });
  };

  const handleChange = (value: TransformObject) => {
    handleApplyTransform(value);
  };

  const onPropertyChange = (value: string, type: string) => {
    const updatedProps = { ...transform, [type]: value };
    handleChange(updatedProps);
  };

  const onMultiplePropertyChange = (types: string[], value: string) => {
    const newValues = types.reduce(
      (accum: Record<string, string>, currentValue: string) => {
        accum[currentValue] = value;
        return accum;
      },
      {},
    );
    handleChange({ ...transform, ...newValues });
  };

  const calculateValue = (
    value: string,
    operation: "add" | "substract" | "multiply",
    operand: number,
    unit?: string,
  ) => {
    const operations = {
      add: `${(Number.parseInt(value, 10) + operand).toString()}`,
      substract: `${(Number.parseInt(value, 10) - operand).toString()}`,
      multiply: `${(Number.parseInt(value, 10) * operand).toString()}`,
    };
    return `${operations[operation]}${unit ?? ""}`;
  };

  const scaleSharedMenuOptions = [
    { label: "110%", value: "110%" },
    { label: "120%", value: "120%" },
    { label: "130%", value: "130%" },
  ];

  const rotateSharedMenuOptions = [
    { label: "90deg", value: "90deg" },
    { label: "180deg", value: "180deg" },
    { label: "270deg", value: "270deg" },
  ];

  const getScaleSimplifiedValue = () => {
    if (isMixedStyleValue(originalTransform)) {
      return null;
    } else if (transform.scaleX !== transform.scaleY) {
      return "100%";
    } else if (transform.scaleX.includes("%")) {
      return transform.scaleX;
    }
    return calculateValue(transform.scaleX, "multiply", 100);
  };

  const getScaleIndividualValue = (type: "X" | "Y" | "Z") => {
    if (isMixedStyleValue(originalTransform)) {
      return null;
    }

    if (transform[`scale${type}`].includes("%")) {
      return transform[`scale${type}`];
    }
    return calculateValue(transform[`scale${type}`], "multiply", 100);
  };

  const toggleGroupOptions: ToggleGroupOption<Transform>[] = [
    {
      label: "Move",
      value: "move",
      metrics: CSS_LENGTH_TYPES,
      inputs: [
        {
          label: "X Axis",
          resetValue: transformDefaultValues.translateX,
          anchorValue: transformDefaultValues.translateX,
          placeholder: transformDefaultValues.translateX,
          onChange: (value: string) => onPropertyChange(value, "translateX"),
          value: transform?.translateX,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.translateX },
          ],
          previewSubProperty: "translateX",
          autofocus: activeTab === "move",
        },
        {
          label: "Y Axis",
          resetValue: transformDefaultValues.translateY,
          anchorValue: transformDefaultValues.translateY,
          placeholder: transformDefaultValues.translateY,
          onChange: (value: string) => onPropertyChange(value, "translateY"),
          value: transform?.translateY,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.translateY },
          ],
          previewSubProperty: "translateY",
        },
        {
          label: "Z Axis",
          resetValue: transformDefaultValues.translateZ,
          anchorValue: transformDefaultValues.translateZ,
          placeholder: transformDefaultValues.translateZ,
          onChange: (value: string) => onPropertyChange(value, "translateZ"),
          value: transform?.translateZ,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.translateZ },
          ],
          previewSubProperty: "translateZ",
        },
      ],
    },
    {
      label: "Scale",
      value: "scale",
      metrics: ["%"],
      inputs: [
        {
          label: "Transform",
          resetValue: transformDefaultValues.scaleX,
          anchorValue: transformDefaultValues.scaleX,
          placeholder: transformDefaultValues.scaleX,
          onChange: (value: string) => {
            return onMultiplePropertyChange(["scaleX", "scaleY"], value);
          },
          value: getScaleSimplifiedValue(),
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.scaleX },
            ...scaleSharedMenuOptions,
          ],
          previewSubProperty: "scaleXY",
          hasDivider: true,
        },
        {
          label: "X Axis",
          resetValue: transformDefaultValues.scaleX,
          anchorValue: transformDefaultValues.scaleX,
          placeholder: transformDefaultValues.scaleX,
          onChange: (value: string) => onPropertyChange(value, "scaleX"),
          value: getScaleIndividualValue("X"),
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.scaleX },
            ...scaleSharedMenuOptions,
          ],
          previewSubProperty: "scaleX",
        },
        {
          label: "Y Axis",
          resetValue: transformDefaultValues.scaleY,
          anchorValue: transformDefaultValues.scaleY,
          placeholder: transformDefaultValues.scaleY,
          onChange: (value: string) => onPropertyChange(value, "scaleY"),
          value: getScaleIndividualValue("Y"),
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.scaleY },
            ...scaleSharedMenuOptions,
          ],
          previewSubProperty: "scaleY",
        },
        {
          label: "Z Axis",
          resetValue: transformDefaultValues.scaleZ,
          anchorValue: transformDefaultValues.scaleZ,
          placeholder: transformDefaultValues.scaleZ,
          onChange: (value: string) => onPropertyChange(value, "scaleZ"),
          value: getScaleIndividualValue("Z"),
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.scaleZ },
            ...scaleSharedMenuOptions,
          ],
          previewSubProperty: "scaleZ",
        },
      ],
    },
    {
      label: "Rotate",
      value: "rotate",
      metrics: ["deg", "rad", "turn"],
      inputs: [
        {
          label: "Transform",
          resetValue: transformDefaultValues.rotateZ,
          anchorValue: transformDefaultValues.rotateZ,
          placeholder: transformDefaultValues.rotateZ,
          onChange: (value: string) => onPropertyChange(value, "rotateZ"),
          value: transform?.rotateZ,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.rotateZ },
            ...rotateSharedMenuOptions,
          ],
          previewSubProperty: "rotateZ",
          hasDivider: true,
        },
        {
          label: "X Axis",
          resetValue: transformDefaultValues.rotateX,
          anchorValue: transformDefaultValues.rotateX,
          placeholder: transformDefaultValues.rotateX,
          onChange: (value: string) => onPropertyChange(value, "rotateX"),
          value: transform?.rotateX,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.rotateX },
            ...rotateSharedMenuOptions,
          ],
          previewSubProperty: "rotateX",
        },
        {
          label: "Y Axis",
          resetValue: transformDefaultValues.rotateY,
          anchorValue: transformDefaultValues.rotateY,
          placeholder: transformDefaultValues.rotateY,
          onChange: (value: string) => onPropertyChange(value, "rotateY"),
          value: transform?.rotateY,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.rotateY },
            ...rotateSharedMenuOptions,
          ],
          previewSubProperty: "rotateY",
        },
        {
          label: "Z Axis",
          resetValue: transformDefaultValues.rotateZ,
          anchorValue: transformDefaultValues.rotateZ,
          placeholder: transformDefaultValues.rotateZ,
          onChange: (value: string) => onPropertyChange(value, "rotateZ"),
          value: transform?.rotateZ,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.rotateZ },
            ...rotateSharedMenuOptions,
          ],
          previewSubProperty: "rotateZ",
        },
      ],
    },
    {
      label: "Skew",
      value: "skew",
      metrics: CSS_ANGLE_TYPES,
      inputs: [
        {
          label: "X Axis",
          resetValue: transformDefaultValues.skewX,
          anchorValue: transformDefaultValues.skewX,
          placeholder: transformDefaultValues.skewX,
          onChange: (value: string) => onPropertyChange(value, "skewX"),
          value: transform?.skewX,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.skewX },
          ],
          previewSubProperty: "skewX",
          autofocus: activeTab === "skew",
        },
        {
          label: "Y Axis",
          resetValue: transformDefaultValues.skewY,
          anchorValue: transformDefaultValues.skewY,
          placeholder: transformDefaultValues.skewY,
          onChange: (value: string) => onPropertyChange(value, "skewY"),
          value: transform?.skewY,
          menuOptions: [
            { label: "Reset", value: transformDefaultValues.skewY },
          ],
          previewSubProperty: "skewY",
        },
      ],
    },
  ];

  const activeGroupOption = toggleGroupOptions.find(
    (option) => option.value === activeTab,
  );

  const transformTitle = !isMixedStyleValue(originalTransform)
    ? getReadableTransformValue(getTransformStyleString(transform))
    : "Mixed";

  return (
    <div className="flex w-full items-center">
      <ModifierLabel label="Transform" />
      <Popover
        isOpen={isOpen}
        shouldPreventDefaultOnInteractOutside={false}
        onOpenChange={setIsOpen}
        title="Transform"
        triggerAsChild
        // NOTE (Sebas, 2024-10-18): This offset is different from the others because this
        // popover is being trigger from an entire input and not from an internal badge.
        sideOffset={84}
        content={
          <div className="flex flex-col gap-2">
            <DeprecatedToggleGroup
              size="sm"
              options={toggleGroupOptions.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              selectedValue={activeTab}
              onChange={(value) => {
                setActiveTab(value as Transform);
              }}
            />
            {activeGroupOption
              ? activeGroupOption.inputs.map(
                  ({
                    label,
                    resetValue,
                    anchorValue,
                    placeholder,
                    value,
                    onChange,
                    menuOptions,
                    previewSubProperty,
                    hasDivider,
                    autofocus,
                  }) => (
                    <div key={label} className="flex flex-col gap-2">
                      <div className="flex flex-row items-end justify-between">
                        <LengthInputSelector
                          metrics={activeGroupOption.metrics}
                          className="col-span-1"
                          label={<ModifierLabel label={label} />}
                          resetValue={resetValue}
                          anchorValue={anchorValue}
                          placeholder={placeholder}
                          value={value!}
                          onChange={onChange!}
                          menuOptions={menuOptions}
                          previewProperty="transform"
                          previewSubProperty={previewSubProperty}
                          autofocus={autofocus}
                          dragTrigger="label"
                          // NOTE (Fran 2024-10-02): Actually Length Input selector does not need
                          // this prop to set a value. It is used for testing or parse the unit, we don't
                          // need that on these fields.
                          field=""
                        />
                      </div>
                      {hasDivider ? <Separator /> : null}
                    </div>
                  ),
                )
              : null}
          </div>
        }
      >
        <div className="flex-1">
          <SelectionIndicator
            className="max-w-40"
            title={transformTitle}
            onClick={() => setIsOpen(true)}
            onReset={() => handleApplyTransform(transformDefaultValues)}
            placeholder="Translate 0px"
            endEnhancer={
              transformTitle.length > 0 && (
                <FormFieldXButton
                  onClick={(e) => {
                    // NOTE (Sebas, 2024-10-10): This is necessary to prevent executing the onClick event of
                    // the parent element.
                    e.stopPropagation();
                    handleApplyTransform(transformDefaultValues);
                  }}
                />
              )
            }
          />
        </div>
      </Popover>
    </div>
  );
};

const TransformOriginControl: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const originalTransformOrigin =
    useEditorSelector(selectTransformOrigin) ?? "";
  const transformOrigin = parseTransformOrigin(originalTransformOrigin);
  const draftComponentId = useEditorSelector(selectDraftComponentId);

  const applyComponentAction = useApplyComponentAction();

  const transformOriginDefaultValues =
    styleAttributeToEditorData.transformOrigin.defaultValue.split(" ");
  const transformOriginX = transformOriginDefaultValues[0]!;
  const transformOriginY = transformOriginDefaultValues[1]!;
  const transformOriginZ = transformOriginDefaultValues[2]!;
  const transformOriginInputs = [
    {
      label: "X Axis",
      title: "transformOriginX" as const,
      resetValue: transformOriginX,
      anchorValue: transformOriginY,
      placeholder: transformOriginZ,
      metrics: CSS_LENGTH_TYPES,
      menuOptions: [
        {
          label: "Reset",
          value: transformOriginX,
        },
        {
          label: "Left",
          value: "0%",
        },
        {
          label: "Center",
          value: "50%",
        },
        {
          label: "Right",
          value: "100%",
        },
      ],
      autofocus: true,
    },
    {
      label: "Y Axis",
      title: "transformOriginY" as const,
      resetValue: transformOriginY,
      anchorValue: transformOriginY,
      placeholder: transformOriginY,
      metrics: CSS_LENGTH_TYPES,
      menuOptions: [
        {
          label: "Reset",
          value: transformOriginY,
        },
        {
          label: "Top",
          value: "0%",
        },
        {
          label: "Center",
          value: "50%",
        },
        {
          label: "Bottom",
          value: "100%",
        },
      ],
    },
    {
      label: "Z Axis",
      title: "transformOriginZ" as const,
      resetValue: transformOriginZ,
      anchorValue: transformOriginZ,
      placeholder: transformOriginZ,
      metrics: CSS_LENGTH_TYPES,
      menuOptions: [
        {
          label: "Reset",
          value: transformOriginZ,
        },
      ],
    },
  ];

  const handleApplyTransformOrigin = (value: string | null) => {
    applyComponentAction({
      componentId: draftComponentId,
      type: "setStyles",
      value: {
        transformOrigin: value,
      },
    });
  };

  const handleChange = (value: string) => {
    handleApplyTransformOrigin(value);
  };

  const internalOnChange = (
    value: string,
    inputName: "transformOriginX" | "transformOriginY" | "transformOriginZ",
  ) => {
    const newValues = {
      ...transformOrigin,
      [inputName]: value,
    };
    handleChange(getTransformOriginString(newValues));
  };

  return (
    <div className="flex w-full items-center">
      <ModifierLabel label="Origin" />
      <Popover
        isOpen={isOpen}
        shouldPreventDefaultOnInteractOutside={false}
        onOpenChange={setIsOpen}
        title="Transform Origin"
        triggerAsChild
        // NOTE (Sebas, 2024-10-18): This offset is different from the others because this
        // popover is being trigger from an entire input and not from an internal badge.
        sideOffset={84}
        content={
          <div className="flex flex-col gap-2">
            {transformOriginInputs.map(
              ({
                label,
                title,
                anchorValue,
                placeholder,
                resetValue,
                menuOptions,
                metrics,
                autofocus = false,
              }) => (
                <LengthInputSelector
                  label={<ModifierLabel label={label} />}
                  key={title}
                  metrics={metrics}
                  className="col-span-1"
                  field={title}
                  resetValue={resetValue}
                  anchorValue={anchorValue}
                  placeholder={placeholder}
                  value={transformOrigin[title]}
                  onChange={(value: string) => internalOnChange(value, title)}
                  menuOptions={menuOptions}
                  previewProperty="transformOrigin"
                  previewSubProperty={title}
                  autofocus={autofocus}
                  dragTrigger="label"
                />
              ),
            )}
          </div>
        }
      >
        <div className="flex-1">
          <SelectionIndicator
            title={
              !isMixedStyleValue(originalTransformOrigin)
                ? "originalTransformOrigin"
                : "Mixed"
            }
            onClick={() => setIsOpen(true)}
            onReset={() => handleApplyTransformOrigin(null)}
            placeholder="0px 0px 0px"
            endEnhancer={
              (isMixedStyleValue(originalTransformOrigin) ||
                String(originalTransformOrigin).length > 0) && (
                <FormFieldXButton
                  onClick={(e) => {
                    // NOTE (Sebas, 2024-10-10): This is necessary to prevent executing the onClick event of
                    // the parent element.
                    e.stopPropagation();
                    handleApplyTransformOrigin(null);
                  }}
                />
              )
            }
          />
        </div>
      </Popover>
    </div>
  );
};

const ShadowControl: React.FC = () => {
  const applyComponentAction = useApplyComponentAction();
  const dispatch = useEditorDispatch();
  const boxShadows = useEditorSelector(selectBoxShadows);
  const openPopoverId = useEditorSelector(selectOpenPopoverId);
  const { colorShopStyles: colorSavedStyles } = useShopStyles();

  if (!boxShadows || boxShadows.length === 0) {
    return null;
  }

  const isPopoverOpen =
    openPopoverId &&
    typeof openPopoverId === "object" &&
    "box-shadow" in openPopoverId;

  const currentShadowIndex = isPopoverOpen ? openPopoverId["box-shadow"] : null;
  const currentShadow =
    currentShadowIndex != null ? boxShadows[currentShadowIndex] : null;

  const onCreate = (value: string) => {
    applyComponentAction({
      type: "setStyles",
      value: {
        boxShadow: value,
      },
    });
  };

  const handleRemoveBoxShadow = (index: number) => {
    // TODO (Martin 2025-01-21): Figure out how to remove a single value since
    // we store shadows as a single string.
    if (boxShadows.some((boxShadow) => isMixedStyleValue(boxShadow))) {
      onCreate("none");
      return;
    }

    const filteredBoxShadows = boxShadows.filter((_, i) => i !== index);
    const updatedBoxShadowes = getBoxShadowString(
      filteredBoxShadows as BoxShadow[],
    );
    onCreate(updatedBoxShadowes);
  };

  const handleBoxShadowChange = (value: BoxShadow, index: number) => {
    // TODO (Martin 2025-01-21): Figure out how to update a single value since
    // we store shadows as a single string.
    if (boxShadows.some((boxShadow) => isMixedStyleValue(boxShadow))) {
      onCreate(getBoxShadowString([value]));
      return;
    }

    const newBoxShadows = boxShadows.map((boxShadow, i) => {
      return i === index ? value : boxShadow;
    });
    const updatedBoxShadows = getBoxShadowString(newBoxShadows as BoxShadow[]);
    onCreate(updatedBoxShadows);
  };

  const getBadgeColor = (color: string) => {
    if (color && isDynamicDesignLibraryValue(color)) {
      const savedStyle = getSavedStyleValue(colorSavedStyles, color);
      return savedStyle?.attributes && "color" in savedStyle.attributes
        ? String(savedStyle.attributes.color)
        : "text-muted";
    }
    return color ?? "text-muted";
  };

  return (
    <div
      // NOTE (Fran 2024-10-16): 74px is the fixed width of the label.
      className="grid w-full grid-cols-[74px,auto] items-center gap-y-2"
    >
      <ModifierLabel label="Shadow" />
      {boxShadows.map((boxShadow, index) => {
        if (!boxShadow) {
          return null;
        }

        const isMixedBoxShadow = isMixedStyleValue(boxShadow);
        return (
          <div className="col-start-2" key={`boxShadow-${index}`}>
            <SelectionIndicator
              className="max-w-40"
              title={
                !isMixedBoxShadow
                  ? formatTitle(boxShadow, colorSavedStyles)
                  : "Mixed"
              }
              onClick={() => {
                return dispatch(setOpenPopoverId({ "box-shadow": index }));
              }}
              startEnhancer={
                <Badge
                  type={!isMixedBoxShadow ? "color" : "unknown"}
                  isFilled
                  backgroundColor={
                    !isMixedBoxShadow
                      ? getBadgeColor(boxShadow.shadowColor)
                      : ""
                  }
                />
              }
              endEnhancer={
                <FormFieldXButton
                  onClick={(e) => {
                    // NOTE (Sebas, 2024-10-10): This is necessary to prevent executing the onClick event to avoid open
                    // the popover when the user wants to remove the shadow.
                    e.stopPropagation();
                    handleRemoveBoxShadow(index);
                  }}
                />
              }
            />
          </div>
        );
      })}
      {currentShadow && (
        <BoxShadowPopover
          isOpen={isPopoverOpen ?? false}
          onRequestClose={() => dispatch(setOpenPopoverId(null))}
          boxShadow={currentShadow}
          onBoxShadowChange={(value) => {
            return handleBoxShadowChange(value, currentShadowIndex ?? 0);
          }}
          boxShadowIndex={currentShadowIndex ?? 0}
        />
      )}
    </div>
  );
};

type BoxShadowPopoverProps = {
  isOpen: boolean;
  boxShadow: BoxShadow | ReploMixedStyleValue;
  onRequestClose: () => void;
  onBoxShadowChange(value: BoxShadow): void;
  boxShadowIndex: number;
};

const BoxShadowPopover: React.FC<BoxShadowPopoverProps> = ({
  isOpen = false,
  onRequestClose,
  boxShadow,
  onBoxShadowChange,
  boxShadowIndex,
}) => {
  const areModalsOpen = useAreModalsOpen();
  return (
    <Popover.Root
      isOpen={isOpen}
      onOpenChange={(newValue: boolean) => {
        if (!newValue) {
          onRequestClose();
        }
      }}
    >
      <Popover.Content
        shouldPreventDefaultOnInteractOutside={areModalsOpen}
        title="Box Shadow"
        onRequestClose={onRequestClose}
      >
        <BoxShadowPopoverContent
          boxShadow={boxShadow}
          onBoxShadowChange={onBoxShadowChange}
          boxShadowIndex={boxShadowIndex}
        />
      </Popover.Content>
      <Popover.Anchor className="relative left-0 top-0" />
    </Popover.Root>
  );
};

const BoxShadowPopoverContent: React.FC<
  Pick<
    BoxShadowPopoverProps,
    "boxShadow" | "onBoxShadowChange" | "boxShadowIndex"
  >
> = ({ boxShadow, onBoxShadowChange, boxShadowIndex }) => {
  const logEvent = useLogAnalytics();
  const isMixedBoxShadow = isMixedStyleValue(boxShadow);
  const draftComponentId = useEditorSelector(selectDraftComponentId);

  const handleInputChange = (
    value: string,
    inputType:
      | "shadowType"
      | "offsetX"
      | "offsetY"
      | "blur"
      | "spread"
      | "shadowColor",
  ) => {
    const newBoxShadow = {
      ...(!isMixedBoxShadow ? boxShadow : DEFAULT_BOX_SHADOW),
      [inputType]: value,
    };
    onBoxShadowChange(newBoxShadow as BoxShadow);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full items-center">
        <ModifierLabel label="Style" />
        <Selectable
          layoutClassName="w-full"
          triggerLayoutClassName="w-full"
          onSelect={(value: string) => handleInputChange(value, "shadowType")}
          value={!isMixedBoxShadow ? boxShadow?.shadowType : boxShadow}
          options={[
            {
              label: "None",
              value: "none",
            },
            {
              label: "Drop Shadow",
              value: "dropShadow",
            },
            { label: "Inset", value: "inset" },
          ]}
        />
      </div>
      <LengthInputSelector
        label={<ModifierLabel label="X Axis" />}
        metrics={CSS_LENGTH_TYPES}
        className="col-span-1"
        field="offsetX"
        resetValue="0px"
        anchorValue="0px"
        placeholder="0px"
        value={!isMixedBoxShadow ? boxShadow?.offsetX : boxShadow}
        onChange={(value: string) => handleInputChange(value, "offsetX")}
        isDisabled={!isMixedBoxShadow && boxShadow?.shadowType === "none"}
        draggingDirection="negative"
        previewProperty="boxShadow"
        previewSubProperty="offsetX"
        previewPropertyIndex={boxShadowIndex}
        autofocus
        dragTrigger="label"
      />
      <LengthInputSelector
        label={<ModifierLabel label="Y Axis" />}
        metrics={CSS_LENGTH_TYPES}
        className="col-span-1"
        field="offsetY"
        resetValue="0px"
        anchorValue="0px"
        placeholder="0px"
        dragTrigger="label"
        draggingDirection="negative"
        value={!isMixedBoxShadow ? boxShadow?.offsetY : boxShadow}
        onChange={(value: string) => handleInputChange(value, "offsetY")}
        isDisabled={!isMixedBoxShadow && boxShadow?.shadowType === "none"}
        previewProperty="boxShadow"
        previewSubProperty="offsetY"
        previewPropertyIndex={boxShadowIndex}
      />
      <LengthInputSelector
        label={<ModifierLabel label="Blur" />}
        metrics={CSS_LENGTH_TYPES}
        className="col-span-1"
        minDragValues={{ px: 0 }}
        minValues={{ px: 0 }}
        field="blur"
        resetValue="0px"
        anchorValue="0px"
        placeholder="0px"
        dragTrigger="label"
        value={!isMixedBoxShadow ? boxShadow?.blur : boxShadow}
        onChange={(value: string) => handleInputChange(value, "blur")}
        isDisabled={!isMixedBoxShadow && boxShadow?.shadowType === "none"}
        previewProperty="boxShadow"
        previewSubProperty="blur"
        previewPropertyIndex={boxShadowIndex}
      />
      <LengthInputSelector
        label={<ModifierLabel label="Spread" />}
        metrics={CSS_LENGTH_TYPES}
        className="col-span-1"
        field="spread"
        resetValue="0px"
        anchorValue="0px"
        placeholder="0px"
        dragTrigger="label"
        value={!isMixedBoxShadow ? boxShadow?.spread : boxShadow}
        onChange={(value: string) => handleInputChange(value, "spread")}
        isDisabled={!isMixedBoxShadow && boxShadow?.shadowType === "none"}
        previewProperty="boxShadow"
        previewSubProperty="spread"
        previewPropertyIndex={boxShadowIndex}
      />
      <div className="flex w-full items-center">
        <ModifierLabel label="Color" />

        <DynamicColorSelector
          field="color"
          componentId={draftComponentId ?? ""}
          allowsGradientSelection={false}
          popoverTitle="Shadow Color"
          value={!isMixedBoxShadow ? boxShadow?.shadowColor : boxShadow}
          onChange={(value: string) => handleInputChange(value, "shadowColor")}
          showSavedStyles
          onSelectSavedStyle={(value: string) => {
            logEvent("library.style.apply", {
              modifier: "shadowColor",
              type: "color",
            });
            handleInputChange(value, "shadowColor");
          }}
          onRemove={() => handleInputChange("", "shadowColor")}
        />
      </div>
    </div>
  );
};

const formatTitle = (
  boxShadow: BoxShadow,
  colorSavedStyles?: (SavedColorStyle & { id: string })[],
) => {
  // NOTE (Fran 2024-12-03): If the color is a dynamic design library value, we need to get the saved style name
  // and replace the color value with the saved style name.
  if (
    boxShadow.shadowColor &&
    isDynamicDesignLibraryValue(boxShadow.shadowColor) &&
    colorSavedStyles
  ) {
    const savedStyle = getSavedStyleValue(
      colorSavedStyles,
      boxShadow.shadowColor,
    );
    return getBoxShadowString(
      [
        {
          ...boxShadow,
          shadowColor: savedStyle?.name ?? "SavedStyle",
        },
      ],
      true,
    );
  }
  const boxShadowTitle = getBoxShadowString([boxShadow], true);
  return boxShadowTitle.includes("none") ? "None" : boxShadowTitle;
};

const useAddNewBoxShadow = () => {
  const store = useEditorStore();
  const applyComponentAction = useApplyComponentAction();
  const dispatch = useEditorDispatch();

  return React.useCallback(() => {
    const boxShadows = selectBoxShadows(store.getState()) ?? [];

    const newBoxShadow = {
      id: uuidv4(),
      ...DEFAULT_BOX_SHADOW,
    };
    // TODO (Martin 2025-01-21): Figure out how to add a single value since
    // we store boxShadows as a single string.
    const hasMixedBoxShadows = boxShadows.some((boxShadow) => {
      return isMixedStyleValue(boxShadow);
    });
    const newBoxShadowString = getBoxShadowString([
      ...(!hasMixedBoxShadows ? (boxShadows as BoxShadow[]) : []),
      newBoxShadow,
    ]);
    applyComponentAction({
      type: "setStyles",
      value: {
        boxShadow: newBoxShadowString,
      },
    });
    dispatch(
      setOpenPopoverId({
        "box-shadow": !isMixedStyleValue(boxShadows) ? boxShadows.length : 0,
      }),
    );
  }, [store, applyComponentAction, dispatch]);
};

export default EffectsModifier;
